import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import reservaService, { CalendarioAdmin } from '@/services/reservaService';
import AdminCalendar from '@/components/admin/AdminCalendar';
import AdminTimeSlots from '@/components/admin/AdminTimeSlots';
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';

const Calendar: React.FC = () => {
  const [calendarioData, setCalendarioData] = useState<CalendarioAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadCalendarioData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await reservaService.getCalendarioAdmin(selectedDate);
      setCalendarioData(data);
    } catch (error) {
      console.error('Erro ao carregar dados do calendário:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadCalendarioData();
  }, [loadCalendarioData]);

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };



  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmado':
        return 'bg-green-100 text-green-800';
      case 'reservado':
        return 'bg-blue-100 text-blue-800';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      case 'concluido':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };



  if (loading) {
    return (
      <ResponsiveAdminLayout
        title="Calendário Administrativo"
        subtitle="Visualização completa das reservas e disponibilidade"
      >
        <div className="flex items-center justify-center py-12">
          <span className="ml-2 text-gray-600">Carregando calendário...</span>
        </div>
      </ResponsiveAdminLayout>
    );
  }

  return (
    <ResponsiveAdminLayout
      title="Calendário Administrativo"
      subtitle="Visualização completa das reservas e disponibilidade"
    >

      {/* Layout responsivo */}
      {isMobile ? (
        // Layout mobile - uma coluna
        <div className="space-y-4">
          {/* Calendário mobile */}
          <Card>
            <CardContent className="p-4">
              <AdminCalendar
                selectedDate={selectedDate}
                onDateSelect={setSelectedDate}
                currentMonth={currentMonth}
                onMonthChange={handleMonthChange}
              />
            </CardContent>
          </Card>

          {/* Horários mobile */}
          <AdminTimeSlots
            calendarioData={calendarioData}
            selectedDate={selectedDate}
            loading={loading}
          />
        </div>
      ) : (
        // Layout desktop - duas colunas
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna 1: Calendário */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Selecionar Data</CardTitle>
              </CardHeader>
              <CardContent>
                <AdminCalendar
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                  currentMonth={currentMonth}
                  onMonthChange={handleMonthChange}
                />
              </CardContent>
            </Card>
          </div>

          {/* Coluna 2: Horários Disponíveis */}
          <div className="lg:col-span-2">
            <AdminTimeSlots
              calendarioData={calendarioData}
              selectedDate={selectedDate}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Legenda */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Legenda</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-sm">Confirmado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-sm">Reservado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
              <span className="text-sm">Pendente</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-sm">Cancelado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
              <span className="text-sm">Concluído</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
              <span className="text-sm">Disponível</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </ResponsiveAdminLayout>
  );
};

export default Calendar;